import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { Refresh as RefreshIcon, Add as AddIcon } from '@mui/icons-material';
import { Account } from '../types/account';
import { accountService } from '../services/api';
import AccountCard from '../components/AccountCard';

const ControlAccounts: React.FC = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadControlAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await accountService.getControlAccounts();
      setAccounts(data);
    } catch (err: any) {
      console.error('Error loading control accounts:', err);
      setError(err.response?.data?.message || 'Failed to load control accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeAccounts = async () => {
    try {
      await accountService.initializeControlAccounts();
      loadControlAccounts();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to initialize control accounts');
    }
  };

  useEffect(() => {
    loadControlAccounts();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Control Accounts</Typography>
        <Box display="flex" gap={1}>
          {accounts.length === 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleInitializeAccounts}
            >
              Initialize Control Accounts
            </Button>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadControlAccounts}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {accounts.length === 0 ? (
        <Alert severity="info">
          <Typography variant="body2">
            No control accounts found. Click "Initialize Control Accounts" to create the standard
            control accounts for the system.
          </Typography>
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {accounts.map((account) => (
            <Grid item xs={12} md={6} lg={4} key={account.id}>
              <AccountCard
                account={account}
                showPartnerInfo={false}
                onClick={() => navigate(`/accounts/${account.accountNumber}`)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ControlAccounts;
